import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQueries } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CardSkeleton } from "@/components/shared/loading-skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import { useDocumentTitle } from "@/lib/hooks/use-document-title";
import { controlFetch } from "@/lib/api/client";
import { queryKeys } from "@/lib/query/keys";
import {
  usePluginManifestsQuery,
  useSaveTenantPluginConfigMutation,
  useSaveTenantPluginCredentialMutation,
  useTenantPluginConfigQuery,
  useValidateTenantPluginMutation,
} from "@/lib/query/plugin-state";
import type {
  GetTenantPluginCredentialHttpResponse,
  PluginControlPlaneManifestRecord,
  TenantPluginConfigRecord,
} from "../../../../../packages/shared-types/src/plugin-control-plane.js";

function isPluginConfigRecord(value: unknown): value is TenantPluginConfigRecord {
  return typeof value === "object" && value !== null && "pluginId" in value && "tenantId" in value;
}

export function PluginDetailPage() {
  const { tenantId: activeTenantId } = useTenantContext();
  const { pluginId: rawPluginId } = useParams<{ pluginId: string }>();
  const pluginId = (rawPluginId ?? "").trim();
  useDocumentTitle(pluginId ? `Plugin ${pluginId}` : "Plugin");

  const { data: manifests = [], isLoading: manifestsLoading } = usePluginManifestsQuery(pluginId);
  const manifest = useMemo(() => manifests[0] ?? null, [manifests]);

  const { data: configRaw, isLoading: configLoading } = useTenantPluginConfigQuery(
    activeTenantId ?? "",
    pluginId,
  );
  const config = isPluginConfigRecord(configRaw) ? configRaw : null;

  const credentialKeys = useMemo(() => manifest?.credentialKeys ?? [], [manifest]);
  const credentialPresence = useQueries({
    queries: credentialKeys.map((credentialKey) => ({
      queryKey: queryKeys.tenantPluginCredential(activeTenantId ?? "", pluginId, credentialKey),
      queryFn: async () =>
        await controlFetch<GetTenantPluginCredentialHttpResponse>("/api/plugins/credential/get", {
          tenantId: activeTenantId,
          pluginId,
          credentialKey,
        }),
      enabled: Boolean(activeTenantId && pluginId && credentialKey),
    })),
  });

  const saveConfigMutation = useSaveTenantPluginConfigMutation();
  const saveCredentialMutation = useSaveTenantPluginCredentialMutation();
  const validateMutation = useValidateTenantPluginMutation();

  const [enabledDraft, setEnabledDraft] = useState<boolean>(() => config?.enabled ?? false);
  const [configJsonDraft, setConfigJsonDraft] = useState<string>(() =>
    JSON.stringify(config?.config ?? {}, null, 2),
  );
  const [credentialDrafts, setCredentialDrafts] = useState<Record<string, string>>({});
  const [hasLocalEdits, setHasLocalEdits] = useState(false);

  useEffect(() => {
    // New tenant/plugin scope => start clean and wait for fetched state.
    setHasLocalEdits(false);
    setEnabledDraft(false);
    setConfigJsonDraft("{}");
    setCredentialDrafts({});
  }, [activeTenantId, pluginId]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (hasLocalEdits) {
      return;
    }
    setEnabledDraft(config.enabled ?? false);
    setConfigJsonDraft(JSON.stringify(config.config ?? {}, null, 2));
  }, [config?.updatedAt, hasLocalEdits, config]);

  if (!activeTenantId) {
    return <EmptyState title="Missing tenant" description="Open this page inside a tenant workspace." />;
  }

  if (!pluginId) {
    return <EmptyState title="Missing plugin id" description="Open a plugin from the Plugins catalog." />;
  }

  if (manifestsLoading || configLoading) {
    return (
      <div className="grid gap-4 xl:grid-cols-2">
        <CardSkeleton />
      </div>
    );
  }

  if (!manifest) {
    return (
      <EmptyState
        title="Plugin not found"
        description={`No manifest found for pluginId=${pluginId}.`}
      />
    );
  }

  const isBusy =
    saveConfigMutation.isPending || saveCredentialMutation.isPending || validateMutation.isPending;

  async function handleSaveConfig() {
    try {
      const parsed = safeParseJsonObject(configJsonDraft);
      await saveConfigMutation.mutateAsync({
        tenantId: activeTenantId,
        pluginId,
        enabled: enabledDraft,
        config: parsed,
      });
      setHasLocalEdits(false);
      toast.success("Plugin config saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save plugin config");
    }
  }

  async function handleValidate() {
    try {
      const result = await validateMutation.mutateAsync({ tenantId: activeTenantId, pluginId });
      if (result.issues.length > 0) {
        throw new Error(result.issues.map((i) => i.message).join(" · "));
      }
      toast.success("Plugin validated");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to validate plugin");
    }
  }

  async function handleSaveCredential(credentialKey: string) {
    const nextValue = (credentialDrafts[credentialKey] ?? "").trim();
    if (!nextValue) {
      toast.error("Secret value is required.");
      return;
    }
    try {
      await saveCredentialMutation.mutateAsync({
        tenantId: activeTenantId,
        pluginId,
        credentialKey,
        secretValue: nextValue,
      });
      setCredentialDrafts((current) => ({ ...current, [credentialKey]: "" }));
      toast.success("Credential saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to save credential");
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <HeaderCard manifest={manifest} enabled={config?.enabled ?? false} />

      <div className="grid gap-4 xl:grid-cols-2">
        <ConfigCard
          enabledDraft={enabledDraft}
          setEnabledDraft={(value) => {
            setHasLocalEdits(true);
            setEnabledDraft(value);
          }}
          configJsonDraft={configJsonDraft}
          setConfigJsonDraft={(value) => {
            setHasLocalEdits(true);
            setConfigJsonDraft(value);
          }}
          onSave={handleSaveConfig}
          onValidate={handleValidate}
          isBusy={isBusy}
        />
        <SetupCard manifest={manifest} />
        <CredentialsCard
          credentialKeys={credentialKeys}
          presence={credentialPresence.map((entry) => entry.data ?? null)}
          drafts={credentialDrafts}
          setDrafts={setCredentialDrafts}
          onSaveCredential={handleSaveCredential}
          isBusy={isBusy}
        />
      </div>
    </div>
  );
}

function HeaderCard(params: {
  manifest: PluginControlPlaneManifestRecord;
  enabled: boolean;
}) {
  return (
    <Card>
      <CardHeader className="gap-2">
        <CardTitle>{params.manifest.name ?? params.manifest.pluginId}</CardTitle>
        <CardDescription>{params.manifest.description ?? "No description provided."}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2 text-xs" style={{ color: "var(--muted-foreground)" }}>
        <Badge variant="outline">{params.manifest.pluginId}</Badge>
        <Badge variant="outline">{params.enabled ? "active" : "not configured"}</Badge>
        <Badge variant="outline">{(params.manifest.providers?.length ?? 0)} providers</Badge>
        <Badge variant="outline">{(params.manifest.channels?.length ?? 0)} channels</Badge>
      </CardContent>
    </Card>
  );
}

function ConfigCard(params: {
  enabledDraft: boolean;
  setEnabledDraft: (value: boolean) => void;
  configJsonDraft: string;
  setConfigJsonDraft: (value: string) => void;
  onSave: () => Promise<void>;
  onValidate: () => Promise<void>;
  isBusy: boolean;
}) {
  return (
    <Card className="h-full">
      <CardHeader className="gap-2">
        <CardTitle className="text-base">Configuration</CardTitle>
        <CardDescription>Tenant-scoped plugin config stored in Postgres.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={params.enabledDraft}
            onChange={(event) => params.setEnabledDraft(event.target.checked)}
          />
          <span>Enabled</span>
        </label>
        <textarea
          value={params.configJsonDraft}
          onChange={(event) => params.setConfigJsonDraft(event.target.value)}
          className="form-input font-mono text-xs min-h-[240px]"
          spellCheck={false}
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void params.onSave()} disabled={params.isBusy}>
            Save config
          </Button>
          <Button size="sm" variant="outline" onClick={() => void params.onValidate()} disabled={params.isBusy}>
            Validate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SetupCard(params: { manifest: PluginControlPlaneManifestRecord }) {
  const activation = params.manifest.activation;
  const setup = params.manifest.setup;

  return (
    <Card className="h-full">
      <CardHeader className="gap-2">
        <CardTitle className="text-base">Setup Checklist</CardTitle>
        <CardDescription>Derived from manifest activation and setup descriptors.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-xs" style={{ color: "var(--muted-foreground)" }}>
        {activation ? (
          <div className="rounded-[var(--radius-sm)] border p-3" style={{ borderColor: "var(--border)", background: "var(--panel)" }}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em]">Activation</div>
            <div className="mt-2 grid gap-1">
              <div>Providers: {(activation.onProviders ?? []).join(", ") || "\u2014"}</div>
              <div>Commands: {(activation.onCommands ?? []).join(", ") || "\u2014"}</div>
              <div>Channels: {(activation.onChannels ?? []).join(", ") || "\u2014"}</div>
              <div>Routes: {(activation.onRoutes ?? []).join(", ") || "\u2014"}</div>
              <div>Capabilities: {(activation.onCapabilities ?? []).join(", ") || "\u2014"}</div>
            </div>
          </div>
        ) : null}

        {setup ? (
          <div className="rounded-[var(--radius-sm)] border p-3" style={{ borderColor: "var(--border)", background: "var(--panel)" }}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em]">Setup</div>
            <div className="mt-2 grid gap-2">
              <div>Requires runtime: {setup.requiresRuntime ? "Yes" : "No"}</div>
              {setup.providers?.length ? (
                <div>
                  <div className="font-medium">Providers</div>
                  <div className="mt-1 grid gap-1">
                    {setup.providers.map((p) => (
                      <div key={p.id} className="font-mono">
                        {p.id}
                        {p.authMethods?.length ? ` auth=${p.authMethods.join(",")}` : ""}
                        {p.envVars?.length ? ` env=${p.envVars.join(",")}` : ""}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {setup.cliBackends?.length ? (
                <div>CLI backends: {setup.cliBackends.join(", ")}</div>
              ) : null}
              {setup.configMigrations?.length ? (
                <div>Config migrations: {setup.configMigrations.join(", ")}</div>
              ) : null}
            </div>
          </div>
        ) : null}

        {!activation && !setup ? (
          <div className="text-xs">No activation/setup descriptors declared by this plugin.</div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CredentialsCard(params: {
  credentialKeys: string[];
  presence: Array<GetTenantPluginCredentialHttpResponse | null>;
  drafts: Record<string, string>;
  setDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  onSaveCredential: (credentialKey: string) => Promise<void>;
  isBusy: boolean;
}) {
  return (
    <Card className="h-full xl:col-span-2">
      <CardHeader className="gap-2">
        <CardTitle className="text-base">Credentials</CardTitle>
        <CardDescription>Tenant-scoped plugin credentials stored in Postgres.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {params.credentialKeys.length === 0 ? (
          <div className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            This plugin does not declare credential keys.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {params.credentialKeys.map((credentialKey, index) => {
              const presence = params.presence[index];
              const hasCredential = presence?.hasCredential ?? false;
              return (
                <div key={credentialKey} className="rounded-[var(--radius-sm)] border p-3" style={{ borderColor: "var(--border)" }}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs font-mono">{credentialKey}</div>
                    <Badge variant="outline">{hasCredential ? "stored" : "missing"}</Badge>
                  </div>
                  <div className="mt-2 flex flex-col gap-2">
                    <input
                      type="password"
                      value={params.drafts[credentialKey] ?? ""}
                      onChange={(event) =>
                        params.setDrafts((current) => ({ ...current, [credentialKey]: event.target.value }))
                      }
                      placeholder={hasCredential ? "Rotate secret (optional)" : "Enter secret"}
                      className="form-input font-mono text-xs"
                    />
                    <Button
                      size="sm"
                      onClick={() => void params.onSaveCredential(credentialKey)}
                      disabled={params.isBusy}
                    >
                      Save credential
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function safeParseJsonObject(raw: string): Record<string, unknown> {
  const trimmed = raw.trim();
  if (!trimmed) {
    return {};
  }
  const parsed = JSON.parse(trimmed) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Config must be a JSON object.");
  }
  return parsed as Record<string, unknown>;
}
